import { useEffect, useState } from "react";
import { sortAges } from "@/lib/ageOrder";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Check, X, MessageSquare, UserPlus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const STATUS = { waiting: "border-amber-500/50 text-amber-300", offered: "border-[#83B68B]/60 text-[#83B68B]", declined: "border-red-500/40 text-red-400" };
const ACTIONS = { offer: "Offer a place", decline: "Decline", note: "Send a note", accept: "Accepted — remove record" };

export default function WaitingList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [age, setAge] = useState("all");
  const [dlg, setDlg] = useState(null); // { entry, action }
  const [form, setForm] = useState({ team_id: "", note: "", send_email: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [w, t] = await Promise.all([api.get("/waiting-list"), api.get("/teams")]);
      setData(w.data); setTeams(t.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load"); }
  };
  useEffect(() => { load(); }, []);

  const open = (entry, action) => { setDlg({ entry, action }); setForm({ team_id: "", note: "", send_email: action !== "accept" }); };
  const submit = async () => {
    if (dlg.action === "accept" && !window.confirm("This permanently removes the child's details from the app (GDPR). Continue?")) return;
    setBusy(true);
    try {
      await api.post(`/waiting-list/${dlg.entry.id}/respond`, { action: dlg.action, team_id: form.team_id || null, note: form.note || null, send_email: form.send_email });
      toast.success(dlg.action === "accept" ? "Accepted and record removed" : `${ACTIONS[dlg.action]} sent`);
      setDlg(null); load(); window.dispatchEvent(new Event("admin-queue-changed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="opacity-50 font-display tracking-widest">LOADING…</div>;
  const rows = data.entries.filter(e => age === "all" || e.age_group === age);
  const ages = sortAges([...new Set(data.entries.map(e => e.age_group))]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Website sign-ups</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">Waiting List</h1>
          <p className="opacity-60 mt-2 text-sm">{isAdmin ? "All year groups." : `Your year groups: ${data.my_age_groups.join(", ") || "none assigned"}.`} Respond to parents here — accepted children are removed from the app automatically.</p>
        </div>
        <div className="flex flex-wrap gap-1" data-testid="wl-age-filter">
          {["all", ...ages].map(a => (
            <button key={a} type="button" onClick={() => setAge(a)} className={`text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border ${age === a ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`} data-testid={`wl-age-${a}`}>{a === "all" ? "All" : a}</button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="wl-empty">No one waiting{age !== "all" ? ` for ${age}` : ""}.</div>
      ) : (
        <div className="space-y-3" data-testid="wl-list">
          {rows.map(e => (
            <div key={e.id} className="card-tactical rounded-md p-4 flex flex-wrap items-start gap-4" data-testid={`wl-entry-${e.id}`}>
              <div className="w-14 text-center shrink-0">
                <div className="font-display font-black text-3xl text-[#83B68B] leading-none">{e.position ?? "—"}</div>
                <div className="text-[9px] tracking-widest uppercase opacity-50 mt-1">in queue</div>
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-lg">{e.child_first_name} {e.child_last_name}</span>
                  <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border border-[#83B68B]/40 text-[#83B68B]">{e.age_group}</span>
                  {e.gender && <span className="text-[10px] tracking-widest uppercase opacity-60">{e.gender}</span>}
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border ${STATUS[e.status] || ""}`} data-testid={`wl-status-${e.id}`}>{e.status}</span>
                  <span className="text-[10px] opacity-50">{e.reference}</span>
                </div>
                <div className="text-xs opacity-70 mt-1 flex flex-wrap gap-x-3">
                  <span>{e.parent_name}</span>
                  <a href={`mailto:${e.parent_email}`} className="hover:text-[#83B68B]">{e.parent_email}</a>
                  {e.parent_phone && <a href={`tel:${e.parent_phone}`} className="hover:text-[#83B68B]">{e.parent_phone}</a>}
                  {e.child_dob && <span>DOB {e.child_dob}</span>}
                  <span>Joined {format(parseISO(e.created_at), "d MMM yyyy")}</span>
                </div>
                {(e.experience || e.notes) && <div className="text-xs opacity-80 mt-2 italic">{[e.experience, e.notes].filter(Boolean).join(" · ")}</div>}
                {(e.history || []).length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {e.history.map((h, i) => <div key={`${h.at}-${i}`} className="text-[11px] opacity-60">{ACTIONS[h.action] || h.action}{h.team_name ? ` · ${h.team_name}` : ""} · {h.by_name} · {format(parseISO(h.at), "d MMM HH:mm")}{h.note ? ` — "${h.note}"` : ""}</div>)}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 shrink-0">
                <button type="button" onClick={() => open(e, "offer")} data-testid={`wl-offer-${e.id}`} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B] hover:text-white inline-flex items-center gap-1"><UserPlus className="w-3 h-3" /> Offer place</button>
                <button type="button" onClick={() => open(e, "note")} data-testid={`wl-note-${e.id}`} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Note</button>
                <button type="button" onClick={() => open(e, "decline")} data-testid={`wl-decline-${e.id}`} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1"><X className="w-3 h-3" /> Decline</button>
                <button type="button" onClick={() => open(e, "accept")} data-testid={`wl-accept-${e.id}`} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded btn-volt inline-flex items-center gap-1"><Check className="w-3 h-3" /> Accepted</button>
                {isAdmin && <button type="button" onClick={async () => { if (!window.confirm("Remove this entry?")) return; await api.delete(`/waiting-list/${e.id}`); load(); }} data-testid={`wl-remove-${e.id}`} className="p-2 opacity-50 hover:opacity-100 hover:text-red-400" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={v => { if (!v) setDlg(null); }}>
        <DialogContent className="max-w-lg">
          {dlg && (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{ACTIONS[dlg.action]} · {dlg.entry.child_first_name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {(dlg.action === "offer" || dlg.action === "accept") && (
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase opacity-70">Team</Label>
                    <Select value={form.team_id || "none"} onValueChange={v => setForm(f => ({ ...f, team_id: v === "none" ? "" : v }))}>
                      <SelectTrigger className="mt-1.5" data-testid="wl-team-select"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">— not specified —</SelectItem>{teams.filter(t => isAdmin || t.age_group === dlg.entry.age_group).map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.age_group}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {dlg.action !== "accept" && (
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase opacity-70">Message to parent {dlg.action === "note" && <span className="text-[#83B68B]">*</span>}</Label>
                    <Textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={4} className="mt-1.5" data-testid="wl-note-input" placeholder={dlg.action === "offer" ? "e.g. Training is Tuesdays 6pm at the Rec — come along next week." : "Your message"} />
                    <label className="inline-flex items-center gap-2 mt-2 text-xs cursor-pointer"><input type="checkbox" checked={form.send_email} onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))} className="accent-[#83B68B]" data-testid="wl-send-email" /> Email the parent</label>
                  </div>
                )}
                {dlg.action === "accept" && <p className="text-sm opacity-80">Marks {dlg.entry.child_first_name} as placed and <strong>permanently deletes</strong> their waiting-list details from the app (GDPR). No email is sent — make sure the parent already knows.</p>}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setDlg(null)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border">Cancel</button>
                  <button type="button" disabled={busy || (dlg.action === "note" && !form.note.trim())} onClick={submit} data-testid="wl-respond-submit" className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40">{busy ? "Sending…" : "Confirm"}</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
